import { useState, useEffect } from 'react'
import { useNetworkState } from '@uidotdev/usehooks'
import { mutationQueue } from '../services/offline/mutationQueue'
import { formPersistence } from '../services/offline/formPersistence'

export interface OfflineStatus {
  isOnline: boolean
  queuedCount: number
  isProcessing: boolean
  draftsCount: number
  lastSync: Date | null
}

export function useOfflineStatus(): OfflineStatus {
  const network = useNetworkState()
  const [queuedCount, setQueuedCount] = useState(0)
  const [isProcessing] = useState(false) // Will be used in Phase 2
  const [draftsCount, setDraftsCount] = useState(0)
  const [lastSync, setLastSync] = useState<Date | null>(null)

  useEffect(() => {
    const checkOfflineStatus = async () => {
      try {
        // Get real queue stats from IndexedDB
        const stats = await mutationQueue.getQueueStats()
        setQueuedCount(stats.pending + stats.processing)

        // Get draft forms count using FormPersistence service
        const drafts = formPersistence.getAllDrafts()
        setDraftsCount(drafts.length)

        // Get last sync time
        const lastSyncTime = localStorage.getItem('resi4rent-last-sync')
        if (lastSyncTime) {
          setLastSync(new Date(lastSyncTime))
        }
      } catch (error) {
        console.error('Failed to check offline status:', error)
        // Fallback to 0 if there's an error
        setQueuedCount(0)
      }
    }

    checkOfflineStatus()

    // Check every 5 seconds
    const interval = setInterval(checkOfflineStatus, 5000)

    return () => clearInterval(interval)
  }, [])

  // Update last sync when coming back online
  useEffect(() => {
    if (network.online && !isProcessing) {
      const now = new Date()
      localStorage.setItem('resi4rent-last-sync', now.toISOString())
      setLastSync(now)
    }
  }, [network.online, isProcessing])

  return {
    isOnline: network.online ?? true,
    queuedCount,
    isProcessing,
    draftsCount,
    lastSync
  }
}
